import { useEffect, useState } from 'react';
import { GradientFilter, ContentWrapper } from './styled';
import { BlurFilter, Button } from '@simplywallst/ui-core';
import {
  useCompanyLimits,
  useGetMutateCompanyLimit,
} from '@/hooks/useCompanyLimits';
import { useSummary } from '@/hooks/useCompany';
import { useTracking } from 'react-tracking';
import { useUserAccess } from '@/hooks/useUserAccess';
import If from '@/components/If';
import loadable from '@loadable/component';
import useUser from '@/hooks/useUser';
import { useTranslation } from '@/hooks/useTranslation';

const PlansModal = loadable(
  () => import('@components/OurPlans/components/PlansModal/PlansModal')
);
const BrazeContentBanner = loadable(
  () => import('@/components/BrazeContentBanner')
);

const BrazeLogLimitModalViewed = loadable(
  () => import('@/components/BrazeCustomEvents'),
  {
    resolveComponent: (c) => c.BrazeLogLimitModalViewed,
  }
);

export type PageState = 'normal' | 'incomplete' | 'restricted';
interface ViewLimitModalProps {
  scrollLimitExceed: boolean;
  selected: string;
  pageState: PageState;
}
type ActiveModal = 'none' | 'plansLimit' | 'viewLimit';

export function ViewLimitModal({
  scrollLimitExceed,
  selected,
  pageState,
}: ViewLimitModalProps) {
  const [activeModal, setActiveModal] = useState<ActiveModal>('none');
  const { mutateCompanyView } = useGetMutateCompanyLimit();
  const summary = useSummary();
  const { trackEvent } = useTracking();
  const limits = useCompanyLimits(
    summary.status === 'success' ? summary.companyId : undefined
  );
  const { data: userAccess } = useUserAccess();
  const { data: user } = useUser();

  useEffect(() => {
    const showPlansModal =
      userAccess.planType === 'free' || user.billing.status === 'trialing'
        ? scrollLimitExceed
        : scrollLimitExceed && selected !== 'summary';

    if (pageState === 'restricted' && showPlansModal && !limits.visited) {
      activeModal !== 'plansLimit' && setActiveModal('plansLimit');
    } else if (
      userAccess.planType === 'premium' &&
      pageState === 'normal' &&
      scrollLimitExceed &&
      selected !== 'summary' &&
      !limits.restricted &&
      !limits.visited &&
      user.billing.status !== 'trialing'
    ) {
      if (summary.status === 'success' && activeModal !== 'viewLimit') {
        trackEvent({
          type: 'track',
          subject: 'page',
          action: 'show',
          modifier: 'viewLimitModal',
          companyId: summary.companyId,
          uniqueSymbol: summary.uniqueSymbol,
          views: limits.views,
          views_limit: limits.limit,
        });
      }
      activeModal !== 'viewLimit' && setActiveModal('viewLimit');
    } else if (
      (activeModal === 'plansLimit' || activeModal === 'viewLimit') &&
      (limits.visited || !scrollLimitExceed || selected === 'summary')
    ) {
      setActiveModal('none');
    }
  }, [
    trackEvent,
    activeModal,
    pageState,
    scrollLimitExceed,
    userAccess.planType,
    limits.restricted,
    limits.visited,
    selected,
    summary,
    user.billing.status,
    limits.views,
    limits.limit,
  ]);

  const t = useTranslation();

  return (
    <If
      conditions={{
        showPlansModal: activeModal === 'plansLimit',
        showLimitModal: activeModal === 'viewLimit',
      }}
    >
      {{
        showPlansModal: () => (
          <>
            <PlansModal>
              {userAccess && (
                <p data-cy-id="plan-restriction">
                  {t('modal.upgrade.viewCount', {
                    current: userAccess.viewCompanyReports,
                    total: userAccess.viewCompanyReports,
                  })}
                </p>
              )}
              <h2>
                {RUNTIME_ENV === 'native'
                  ? t('modal.upgrade.heading.native')
                  : t('modal.upgrade.heading.web')}
              </h2>
              <BrazeContentBanner
                feedType="upsell_modal"
                anchorTarget="_blank"
              />
            </PlansModal>
            <BrazeLogLimitModalViewed />
          </>
        ),
        showLimitModal: () => (
          <>
            <BlurFilter blurAmount={20} filterId={'company-report'} />
            <GradientFilter />
            <ContentWrapper>
              <Button
                data-cy-id="view-limit-modal"
                styleType="louderLarge"
                onClick={() => {
                  mutateCompanyView(
                    summary.status === 'success' ? summary.companyId : undefined
                  );

                  if (summary.status === 'success') {
                    trackEvent({
                      type: 'track',
                      subject: 'page',
                      action: 'click',
                      modifier: 'viewLimitModal',
                      companyId: summary.companyId,
                      uniqueSymbol: summary.uniqueSymbol,
                    });
                  }
                }}
              >
                View Full Report
              </Button>
              {limits.status === 'success' && (
                <p data-cy-id="plan-restriction">
                  {'You have viewed'} <span>{limits.views}</span>
                  {` / ${limits.limit} full reports over 30 days`}
                </p>
              )}
            </ContentWrapper>
          </>
        ),
      }}
    </If>
  );
}
